import { SetStateAction, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getStudentProfile } from "../../api/Students/StudentProfile";
import imagePlaceholder from "../../assets/ImgPlaceholder.svg";
import { useParams } from "react-router-dom";
import MailIcon from "../../components/Icons/MailIcon";
import CourseCard from "../../components/Dashboard/Students/CourseCard";
import { Course } from "../../types/types";
import Spinner from "../../components/Shared/Spinner";

const UserProfile = () => {
    const { id } = useParams();
    const studentId = id || "";
    const { data: StudentProfile, isLoading: isStudentProfileLoading } = useQuery(
        {
            queryFn: () => getStudentProfile(studentId),
            queryKey: ["studentprofile", studentId],
            staleTime: 0,
            refetchInterval: 0,
        }
    );
    const [activeTab, setActiveTab] = useState("courses");
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 9;

    const student = StudentProfile?.data?.student;
    const courses = StudentProfile?.data?.courses || [];
    const quizez = StudentProfile?.data?.quizez || [];

    const totalPages = Math.ceil(quizez.length / itemsPerPage);

    const handlePageChange = (pageNumber: SetStateAction<number>) => {
        setCurrentPage(pageNumber);
    };

    const displayedQuizzes = quizez.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="py-7 container">
            <h1 className="text-burntBlack font-bold text-[32px] mb-7">
                Student Profile
            </h1>
            <div className="p-14 rounded-xl bg-white border border-black/15 shadow">
                {isStudentProfileLoading ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className="flex gap-7 items-center pb-9 border-b-2 border-black/15">
                            <img
                                src={student?.img_url || imagePlaceholder}
                                alt="student profile"
                                className="size-[120px] object-cover object-[75%_25%] rounded-full"
                            />
                            <div>
                                <h1 className="font-bold text-[32px] mb-3">
                                    {student?.firstName} {student?.lastName}{" "}
                                    <sub className="text-sm">({student?.phone})</sub>
                                </h1>
                                <div className="flex gap-1.5 items-center w-fit py-2 px-3 bg-[#F3F4F6] rounded-full">
                                    <MailIcon width={16} height={16} />
                                    <p className="text-[#565E6C] text-sm">{student?.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="flex gap-4 mt-6 w-full">
                            <button
                                className={`py-2 px-4 h-14 text-xl font-semibold rounded-xl w-full text-center transition-all duration-300 ${activeTab === "courses"
                                    ? "bg-primary text-white"
                                    : "bg-gray-200 text-black"
                                    }`}
                                onClick={() => setActiveTab("courses")}
                            >
                                Courses
                            </button>
                            <button
                                className={`py-2 px-4 h-14 text-xl font-semibold rounded-xl w-full text-center transition-all duration-300 ${activeTab === "quizs"
                                    ? "bg-primary text-white"
                                    : "bg-gray-200 text-black"
                                    }`}
                                onClick={() => setActiveTab("quizs")}
                            >
                                Quiz Progress
                            </button>
                        </div>

                        <div className="pt-5">
                            {activeTab === "courses" && (
                                <div className="space-y-8">
                                    <h1 className="font-bold text-sm">{courses.length} items</h1>
                                    <div className="grid grid-cols-3 place-content-between gap-6">
                                        {courses.map((course: Course) => (
                                            <div className="flex flex-col">
                                                <CourseCard
                                                    studentId={`${student?.id}`}
                                                    key={course.id}
                                                    course={course}
                                                />
                                                <p className="sm:mb-3 mb-2.5 sm:pb-0.5 text-lightGray tracking-[0.02em] text-sm sm:text-lg font-medium mt-5">
                                                    Student Progress -{" "}
                                                    {parseFloat(`${course.progress.precentage}`).toFixed(2)}%
                                                </p>
                                                <div className="w-full h-2.5 rounded-[30px] bg-[#D9D9D9] overflow-hidden mb-5 sm:mb-6">
                                                    <div
                                                        style={{ width: `${course.progress.precentage}%` }}
                                                        className="bg-primary h-full"
                                                    ></div>
                                                </div>
                                                <h1>Videos Watched : {course.progress.videoWatched} of {course.progress.totalVideos}</h1>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {activeTab === "quizs" && (
                                <div>
                                    <div className="grid grid-cols-3 gap-5">
                                        {displayedQuizzes.map((quiz, index) => (
                                            <div
                                                className="col-span-1 border-black/15 border rounded-lg px-5 py-10 text-center space-y-4"
                                                key={index}
                                            >
                                                <h1 className="font-bold text-primary text-xl">
                                                    {quiz.quizName}
                                                </h1>
                                                <h2 className="font-medium">Num of Trial: {quiz.num_tries}</h2>
                                                <h3 className="font-semibold">
                                                    Student Score: {quiz.score.toFixed(2)}%
                                                </h3>
                                                <h4
                                                    className={`font-semibold ${quiz.status === "passed"
                                                        ? "text-green-500"
                                                        : "text-red-500"
                                                        }`}
                                                >
                                                    Status: {quiz.status}
                                                </h4>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-center mt-8 space-x-2">
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <button
                                                key={index}
                                                className={`py-1 px-3 rounded ${currentPage === index + 1
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-gray-200 text-black"
                                                    }`}
                                                onClick={() => handlePageChange(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>

                )}
            </div>
        </div>
    );
};

export default UserProfile;
